/**
 * How big the PV field really is.
 *
 * The panel draws a slice - five rows of twenty - but an obstacle shades some
 * rows and not others, so "what fraction of the array" is a question about the
 * whole field. This is the one place that answers it, so a drawing preference
 * can never reach a number the simulation applies.
 */

/**
 * How big the array really is, for measuring against - not for drawing.
 *
 * Returns null when the module count is unknown, and the caller then falls back
 * to the drawn slice and says so, rather than inventing a field.
 */
export function deriveField({ totalModules, pitch, moduleWidth, verticalModules, fieldRows, rowModules }) {
  const n = Number(totalModules);
  if (!(n > 0) || !(pitch > 0) || !(moduleWidth > 0)) return null;

  // The rows given one by one -- by the parcel fit, or typed row by row: one
  // entry per table, in module columns. Then there is nothing to guess or to
  // share out, and the capacity those rows hold may differ from the module
  // count the strings add up to. That difference is a finding, not something
  // to hide by stretching rows, so it is left for the string check to report.
  const fromParcel = Array.isArray(rowModules)
    ? rowModules.map((c) => Number(c)).filter((c) => Number.isInteger(c) && c > 0)
    : null;
  if (fromParcel && fromParcel.length > 0) {
    const modulesFull = Math.max(...fromParcel);
    const shortRows = fromParcel.filter((c) => c !== modulesFull);
    return {
      rows: fromParcel.length,
      rowsGiven: true,
      explicit: true,
      modulesAlong: modulesFull,
      widthAcross: fromParcel.length * pitch,
      rowModules: fromParcel,
      rowsUsed: fromParcel.length,
      modulesFull,
      shortModules: shortRows.length ? Math.min(...shortRows) : 0,
      divides: shortRows.length === 0,
      capacity: fromParcel.reduce((a, c) => a + c, 0) * (Number(verticalModules) || 1),
      rowLength: modulesFull * moduleWidth,
      sampledRows: Math.min(fromParcel.length, 12),
    };
  }
  // How many rows the field is divided into. Told, when the layout says so -
  // then the row length is arithmetic rather than a guess: the modules are
  // shared equally between the rows and each row is as long as its share.
  // Otherwise assumed, by taking the row count that makes the field square,
  // which is a guess and is labelled as one wherever the number is shown.
  const set = Number(fieldRows);
  const given = Number.isInteger(set) && set > 0 && set * verticalModules <= n;
  const rows = given
    ? set
    : Math.max(1, Math.round(Math.sqrt((n * moduleWidth) / (pitch * verticalModules))));
  // Modules rarely divide evenly between rows, and a field is not built by
  // sharing the remainder out. It is built with full rows until the modules run
  // short, and one last row that gets what is left: 1728 over 30 rows one high
  // is 29 rows of 58 and one of 46, not thirty rows of 57.6.
  //
  // Rounding that to "58 a row" builds a 1740-module array - twelve modules
  // that do not exist - and since the row length is the denominator of every
  // obstacle fraction, that bias lands straight in the shading table. So the
  // rows are enumerated instead, and every one of them is a real row.
  const columnsFull = Math.max(1, Math.ceil(n / (rows * verticalModules)));
  const fullRows = Math.max(0, Math.floor(n / (verticalModules * columnsFull)));
  const leftover = n - fullRows * columnsFull * verticalModules;
  const shortColumns = leftover > 0 ? Math.round(leftover / verticalModules) : 0;
  const enumerated = [];
  for (let i = 0; i < Math.min(fullRows, rows); i += 1) enumerated.push(columnsFull);
  if (shortColumns > 0 && enumerated.length < rows) enumerated.push(shortColumns);
  const modulesAlong = columnsFull;

  return {
    rows,
    rowsGiven: given,
    modulesAlong,
    // How wide the field is across the rows - the other side of the rectangle
    // an obstacle is measured against.
    widthAcross: rows * pitch,
    // Every row of the field, in columns. The last one is short when the
    // modules do not divide; there is never more than one.
    rowModules: enumerated,
    rowsUsed: enumerated.length,
    modulesFull: columnsFull,
    shortModules: shortColumns,
    divides: shortColumns === 0,
    // The full row - what the ground has to be sized around, and what a row
    // is unless it is the last one.
    rowLength: columnsFull * moduleWidth,
    // Sampling every row of a 40-row field costs time for no accuracy: the rows
    // an obstacle shades are contiguous, so an evenly spaced subset lands the
    // fraction within a percent. Capped, and the cap is reported.
    sampledRows: Math.min(rows, 12),
  };
}

/** Ground area reserved by the current layout, including its planning margin. */
export function estimateFieldArea({ totalModules, moduleLength, moduleWidth, verticalModules, fieldRows, pitch, tiltDeg = 0, portrait = true, marginFactor = 1.1 }) {
  const count = Number(totalModules);
  const tiers = Number(verticalModules);
  const width = Number(moduleWidth);
  const length = Number(moduleLength);
  const margin = Number(marginFactor);
  if (!(count > 0 && tiers > 0 && width > 0 && margin > 0)) return null;

  if (Number(fieldRows) === 1) {
    if (!(length > 0)) return null;
    const alongRow = portrait ? width : length;
    const alongSlope = portrait ? length : width;
    const tilt = Number(tiltDeg);
    if (!Number.isFinite(tilt) || tilt < 0 || tilt >= 90) return null;
    const rowLength = Math.ceil(count / tiers) * alongRow;
    const rowDepth = tiers * alongSlope * Math.cos((tilt * Math.PI) / 180);
    return rowLength * rowDepth * margin;
  }

  const rowPitch = Number(pitch);
  if (!(rowPitch > 0)) return null;
  // Preserve the established multi-row area estimate; only the one-row case
  // stops using pitch, since there is no second row to measure it against.
  return (width * count * rowPitch * margin) / tiers;
}

/**
 * The field's rows in one phrase: "29 × 58 + 1 × 46 modules".
 *
 * Written out rather than averaged, because the average is a row that does not
 * exist. A reader can check this against the plant: twenty-nine rows they can
 * count, and one short one at the end.
 */
export function describeRows(field) {
  if (!field || !Array.isArray(field.rowModules) || field.rowModules.length === 0) {
    return "";
  }
  if (field.explicit) {
    // Rows set one by one -- by the parcel or by hand -- are whatever they are,
    // so they are listed by length rather than forced into "full + one short".
    const counts = new Map();
    field.rowModules.forEach((c) => counts.set(c, (counts.get(c) || 0) + 1));
    return [...counts.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([cols, times]) => `${times} × ${cols}`)
      .join(" + ") + " modules, set row by row";
  }
  const full = field.rowModules.filter((m) => m === field.modulesFull).length;
  const head = `${full} × ${field.modulesFull}`;
  return field.divides
    ? `${head} modules`
    : `${head} + 1 × ${field.shortModules} modules`;
}
